import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Wastage reason codes — aligned with backend WastageReason enum (FR-ORD-046) */
export type WastageType =
  | 'EXPIRED'
  | 'OVERCOOKED'
  | 'SPILLAGE'
  | 'DAMAGED'
  | 'OTHER'
  | 'SPOILAGE'
  | 'OVERPRODUCTION'
  | 'PREPARATION_LOSS';

/** Units for wastage quantity */
export type WastageUnit = 'KG' | 'GM' | 'LTR' | 'ML' | 'PCS';

/** Kitchen station options */
export type KitchenStation = 'Grill' | 'Bar' | 'Tandoor' | 'Dessert' | 'Main Kitchen';

// ── Summary types ────────────────────────────────────────────────────────────

export interface WastageReasonSummary {
  reason: WastageType;
  item_count: number;
  total_quantity: number;
  total_cost: number;
}

export interface WastageProductSummary {
  product_id: string;
  product_name: string;
  item_count: number;
  total_quantity: number;
  total_cost: number;
}

export interface WastageDateSummary {
  date: string;
  log_count: number;
  item_count: number;
  total_quantity: number;
  total_cost: number;
}

export interface WastageSummary {
  total_quantity: number;
  total_cost: number;
  wastage_percent_of_sales: number;
  by_reason: WastageReasonSummary[];
  by_product: WastageProductSummary[];
  by_date: WastageDateSummary[];
}

// ── Individual wastage entry (for DataTable) ─────────────────────────────────

export interface WastageEntry {
  id: string;
  wastage_date: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit: WastageUnit;
  wastage_type: WastageType;
  cost: number;
  reason: string;
  station: string | null;
  photo_url: string | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
  /** QA IS4-002: PENDING_APPROVAL holds the stock until approved; REJECTED moved nothing. */
  status?: WastageStatus;
}

export type WastageStatus = 'POSTED' | 'PENDING_APPROVAL' | 'REJECTED';

/** The owner's wastage limits (QA IS4-002), in minor units of the tenant currency. */
export interface WastageLimits {
  cashier_record_limit: number;
  /** null = no limit */
  manager_approve_limit: number | null;
  is_default?: boolean;
}

export interface WastageListResponse {
  data: WastageEntry[];
  meta: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// ── Create request types ─────────────────────────────────────────────────────

export interface WastageItemRequest {
  product_id: string;
  quantity: number;
  wastage_type: WastageType;
  reason?: string;
  unit?: WastageUnit;
  wastage_date?: string;
  station?: string;
  photo_url?: string;
}

interface CreateWastageRequest {
  location_id: string;
  items: WastageItemRequest[];
}

export interface WastageLogResponse {
  id: string;
  location_id: string;
  created_by: string;
  created_at: string;
  status?: WastageStatus;
  /** The entry's value (minor units). */
  amount?: number;
  /** When the entry waits: the recorder's limit it went over. */
  limit?: number | null;
  items: Array<{
    id: string;
    product_id: string;
    product_name: string;
    quantity: number;
    unit: string;
    unit_cost: number;
    wastage_type: string;
    reason: string;
    station: string | null;
    photo_url: string | null;
  }>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service (API paths per PAGES.md §3.90) ─────────────────────────────────

export const wastageService = {
  /** GET /api/v1/inventory/wastage — paginated wastage records */
  async list(params: {
    location_id?: string;
    from?: string;
    to?: string;
    reason?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<WastageListResponse> {
    const result = await apiClientPaginated<WastageEntry[]>(`/inventory/wastage${buildQuery({
      location_id: params.location_id,
      from: params.from,
      to: params.to,
      reason: params.reason,
      page: params.page,
      limit: params.limit,
    })}`);
    return {
      data: result.data,
      meta: {
        total: result.meta.total,
        page: result.meta.page,
        limit: result.meta.limit,
        // The API sends snake_case `total_pages`; apiClientPaginated's camelCase type
        // does not match it, and reading `totalPages` stopped the export at page 1.
        total_pages: (result.meta as unknown as { total_pages: number }).total_pages,
      },
    };
  },

  /** GET /api/v1/inventory/wastage/summary — aggregated summary */
  async getSummary(params: {
    location_id?: string;
    from?: string;
    to?: string;
    reason?: string;
  } = {}): Promise<WastageSummary> {
    return apiClient<WastageSummary>(`/inventory/wastage/summary${buildQuery({
      location_id: params.location_id,
      from: params.from,
      to: params.to,
      reason: params.reason,
    })}`);
  },

  /** POST /api/v1/inventory/wastage — record wastage */
  async create(dto: CreateWastageRequest): Promise<WastageLogResponse> {
    return apiClient<WastageLogResponse>('/inventory/wastage', { method: 'POST', body: dto });
  },

  /** GET /api/v1/inventory/wastage/limits — the owner's wastage limits */
  async getLimits(): Promise<WastageLimits> {
    return apiClient<WastageLimits>('/inventory/wastage/limits');
  },

  /** PATCH /api/v1/inventory/wastage/limits — OWNER only */
  async setLimits(dto: { cashier_record_limit: number; manager_approve_limit: number | null }): Promise<WastageLimits> {
    return apiClient<WastageLimits>('/inventory/wastage/limits', { method: 'PATCH', body: dto });
  },
};
